import { FC, useEffect, useMemo, useState, type JSX } from 'react';
import {
  ImageType,
  OfferedProductType,
  OfferTypeEnum,
  OptionType,
  WantedProductType,
} from '../types/SellTradeForm.types';
import { brandApis } from '../apis/brandApis';
import { modelApis } from '../apis/modelApis';
import { WatchesSellForm } from './WatchesSellForm';
import { WatchesTradeForm } from './WatchesTradeForm';
import { currencyList } from '../mock';
import { FilterOptionEnum } from '../../Catalog/types';
import { IProduct } from '../../../interfaces/product';
import _ from 'lodash';
import { Button } from '@mui/material';
import { useAppSelector } from '../../../hooks/reduxHooks';

export type Props = {
  offeredProducts: OfferedProductType[];
  wantedProducts: WantedProductType[];
  manageOfferedProducts: (
    propName: keyof OfferedProductType,
    payload: string | number | boolean | JSX.Element | ImageType[],
    index: number
  ) => void;
  manageWantedProducts: (
    propName: keyof WantedProductType,
    payload: string | number | boolean | JSX.Element | ImageType[],
    index: number
  ) => void;
  addProduct: (productType: string) => void;
  applyOffer: (offerType: OfferTypeEnum | undefined) => void;
  offerType: OfferTypeEnum;
  forModal?: boolean;
  loading: boolean;
  removeImage: (image: ImageType, index: number) => void;
  step?: number;
  setStep?: (payload: number) => void;
};

export const WatchesForm: FC<Props> = ({
  offeredProducts,
  wantedProducts,
  manageOfferedProducts,
  manageWantedProducts,
  addProduct,
  applyOffer,
  offerType,
  forModal,
  loading,
  removeImage,
  step,
  setStep,
}) => {
  const [brandsWanted, setBrandsWanted] = useState<OptionType[]>([]);
  const [brandsOffered, setBrandsOffered] = useState<OptionType[]>([]);
  const [modelsWanted, setModelsWanted] = useState<OptionType[][]>([]);
  const [modelsOffered, setModelsOffered] = useState<OptionType[][]>([]);
  const locale = useAppSelector((st) => st.locale);
  const currencyOptions = useMemo(
    () =>
      Object.keys(currencyList)?.map((key) => ({
        id: key,
        label: key,
      })) || [],
    []
  );

  useEffect(() => {
    getBrands('both');
    getModels('both', 0);
  }, []);

  async function getBrands(which: string) {
    try {
      const response = await brandApis.getAll();
      switch (which) {
        case 'wanted':
          setBrandsWanted(
            _.sortBy(
              response?.map((item: any) => ({
                id: item.name,
                label: item.name,
              })) || [],
              'label'
            )
          );
          return;
        case 'offered':
          setBrandsOffered(
            _.sortBy(
              response?.map((item: any) => ({
                id: item.name,
                label: item.name,
              })) || [],
              'label'
            )
          );
          return;
        case 'both':
          setBrandsWanted(
            _.sortBy(
              response?.map((item: any) => ({
                id: item.name,
                label: item.name,
              })) || [],
              'label'
            )
          );
          setBrandsOffered(
            _.sortBy(
              response?.map((item: any) => ({
                id: item.name,
                label: item.name,
              })) || [],
              'label'
            )
          );
          return;
      }
    } catch (e) {
      console.log(e);
    }
  }

  async function getModels(which: string, index: number, brandName?: string) {
    let url = '';
    if (brandName) {
      url = url.concat(
        `filter[column]=BrandName&filter[value]=${brandName.replaceAll('&', '%26')}&filter[option]=${
          FilterOptionEnum.IsEqualTo
        }`
      );
    }
    try {
      const response = brandName ? await modelApis.getByBrand(url) : await modelApis.getAll();
      let set: string[] = Array.from(
        new Set(response?.filter((item: IProduct) => item?.model?.name)?.map((item: IProduct) => item?.model?.name))
      );
      const fetchedOptions =
        set?.sort()?.map((item: string) => ({
          id: undefined,
          label: item,
        })) || [];
      switch (which) {
        case 'wanted':
          setModelsWanted((prev: OptionType[][]) =>
            prev?.[index]
              ? prev.map((options, i) => (i !== index ? options : fetchedOptions))
              : [...prev, fetchedOptions]
          );
          return;
        case 'offered':
          setModelsOffered((prev: OptionType[][]) =>
            prev?.[index]
              ? prev.map((options, i) => (i !== index ? options : fetchedOptions))
              : [...prev, fetchedOptions]
          );
          return;
        case 'both':
          setModelsWanted((prev: OptionType[][]) =>
            prev?.[index]
              ? prev.map((options, i) => (i !== index ? options : fetchedOptions))
              : [...prev, fetchedOptions]
          );
          setModelsOffered((prev: OptionType[][]) =>
            prev?.[index]
              ? prev.map((options, i) => (i !== index ? options : fetchedOptions))
              : [...prev, fetchedOptions]
          );
          return;
      }
    } catch (e) {
      console.log(e);
    }
  }

  function handleBlur(key: string, value: string, which: string, index: number, request?: boolean) {
    if (!value) return;
    switch (which) {
      case 'wanted':
        if (key === 'brandName') {
          setBrandsWanted((prev) =>
            _.sortBy(
              prev.findIndex((item) => item.label === value) === -1 ? [...prev, { id: value, label: value }] : prev,
              'label'
            )
          );
          request && setModelsWanted((prev) => prev?.map((options, i) => (i === index ? [] : options)));
          request && getModels(which, index, value);
        } else if (key === 'productName') {
          setModelsWanted((prev) =>
            prev.map((options, i) =>
              i !== index
                ? options
                : (() => {
                    if (options?.findIndex((item) => item.label === value) === -1) {
                      return _.sortBy([...options, { id: value, label: value }], 'label');
                    } else {
                      return _.sortBy(options, 'label');
                    }
                  })()
            )
          );
        }
        return;
      case 'offered':
        if (key === 'brandName') {
          setBrandsOffered((prev) =>
            _.sortBy(
              prev.findIndex((item) => item.label === value) === -1 ? [...prev, { id: value, label: value }] : prev,
              'label'
            )
          );
          request && setModelsOffered((prev) => prev?.map((options, i) => (i === index ? [] : options)));
          request && getModels(which, index, value);
        } else if (key === 'productName') {
          setModelsOffered((prev) =>
            prev.map((options, i) =>
              i !== index
                ? options
                : (() => {
                    if (options?.findIndex((item) => item.label === value) === -1) {
                      return _.sortBy([...options, { id: value, label: value }], 'label');
                    } else {
                      return _.sortBy(options, 'label');
                    }
                  })()
            )
          );
        }
        return;
    }
  }

  return (
    <form className='form-container'>
      {offerType === OfferTypeEnum.Sell || offerType === OfferTypeEnum.Commission ? (
        <WatchesSellForm
          brands={brandsOffered}
          models={modelsOffered}
          currencyOptions={currencyOptions}
          offeredProducts={offeredProducts}
          manageOfferedProducts={manageOfferedProducts}
          addProduct={addProduct}
          handleBlur={handleBlur}
          removeImage={removeImage}
        />
      ) : offerType === OfferTypeEnum.Trade ? (
        <WatchesTradeForm
          addProduct={addProduct}
          brandsWanted={brandsWanted}
          brandsOffered={brandsOffered}
          modelsWanted={modelsWanted}
          modelsOffered={modelsOffered}
          currencyOptions={currencyOptions}
          offeredProducts={offeredProducts}
          wantedProducts={wantedProducts}
          manageOfferedProducts={manageOfferedProducts}
          manageWantedProducts={manageWantedProducts}
          forModal={forModal}
          handleBlur={handleBlur}
          removeImage={removeImage}
          step={step}
          setStep={setStep}
        />
      ) : null}

      {(offerType === 1 && step === 3) || offerType !== 1 || forModal ? (
        <Button
          style={{ height: '48px', margin: forModal ? '18px 0 48px' : '30px 0 150px' }}
          onClick={() => applyOffer(offerType)}
          variant='outlined'
        >
          {locale['submit']}
        </Button>
      ) : (
        <Button
          style={{ height: '48px', margin: forModal ? '18px 0 48px' : '30px 0 150px' }}
          onClick={() => (setStep ? setStep(3) : null)}
          variant='outlined'
        >
          Next
        </Button>
      )}
    </form>
  );
};
